import React from "react";
import { Modal, Tooltip, Tag } from "antd";
import { GlobalOutlined, ClockCircleOutlined, HomeOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useTimeZone, TIMEZONE_OPTIONS, getTimezoneByMode } from "../../contexts/TimeZoneContext";
import { formatFlightTime, isSameDay } from "../../utils/timezoneUtils";

// 加载dayjs插件
dayjs.extend(utc);
dayjs.extend(timezone);

// 样式定义
const styles = {
    timezoneDisplay: {
        fontFamily: "'Times New Roman', 'JetBrains Mono', 'Fira Mono', 'Menlo', 'Consolas', 'monospace'",
        display: "inline-flex",
        alignItems: "baseline",
        position: "relative",
    },
    timeText: {
        fontFamily: "'Times New Roman', 'JetBrains Mono', 'Fira Mono', 'Menlo', 'Consolas', 'monospace'",
        fontWeight: 700,
    },
    timezoneIndicator: {
        fontSize: "1em",
        fontWeight: "normal",
        marginLeft: "2px",
        marginRight: "2px",
        verticalAlign: "super",
        position: "relative",
    },
    crossDayMark: {
        fontSize: "0.67em",
        fontWeight: "bold",
        verticalAlign: "super",
        marginLeft: "2px",
        position: "relative",
    },
};

const detailTriggerStyle = {
    padding: 0,
    border: 0,
    background: "transparent",
    color: "inherit",
    font: "inherit",
    cursor: "pointer",
};

let activeFlightTimeDetailModal = null;

function openFlightTimeDetail(title, content) {
    activeFlightTimeDetailModal?.destroy?.();
    activeFlightTimeDetailModal = Modal.info({
        title,
        content,
        okText: "关闭",
        maskClosable: true,
        afterClose: () => {
            activeFlightTimeDetailModal = null;
        },
    });
}

function FlightTimeDetailTrigger({ children, title, content }) {
    return (
        <button
            type="button"
            style={detailTriggerStyle}
            aria-label="查看时间详情"
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                openFlightTimeDetail(title, content);
            }}
        >
            {children}
        </button>
    );
}

/**
 * 检查两个时间是否在同一天（在指定时区下）
 * @param {string} time1 - 第一个时间
 * @param {string} time2 - 第二个时间
 * @param {string} timeZone - 时区模式
 * @param {string} airportCode - 机场代码
 * @param {object} airportsMap - 机场数据映射
 * @returns {boolean} 是否在同一天
 */
const areTimesOnSameDay = (time1, time2, timeZone, airportCode, airportsMap) => {
    if (!time1 || !time2) return true; // 如果任一时间为空，默认认为是同一天

    try {
        // 获取目标时区
        const targetTimezone = getTimezoneByMode(timeZone, airportCode, airportsMap);

        // 使用 timezoneUtils 中的 isSameDay 函数
        return isSameDay(time1, time2, targetTimezone);
    } catch (error) {
        console.error("检查同一天错误:", error, { time1, time2, timeZone, airportCode });
        return true; // 出错时默认认为是同一天
    }
};

/**
 * 航班时间显示组件，支持不同时区和跨日标记
 * @param {string} utcTime - UTC时间字符串
 * @param {string} airportCode - 机场代码
 * @param {object} airportsMap - 机场数据映射
 * @param {string} baseUtcTime - 基准UTC时间（用于计算跨天标记，如STD对应的STA）
 * @param {string} format - 时间格式，默认为"HH:mm"
 * @param {boolean} showTimezone - 是否显示时区标签
 * @param {boolean} showDate - 是否显示日期，为true时会在format中添加"MM-DD"
 * @param {string} stdTime - STD时间，用于跨天检测（可选）
 * @returns {JSX.Element} 时区感知时间显示组件
 */
const FlightTimeDisplay = ({
    utcTime,
    airportCode,
    airportsMap = {},
    baseUtcTime = null,
    format = "HH:mm",
    showTimezone = true,
    showDate = false,
    stdTime = null, // 新增参数：STD时间，用于跨天检测: 如果stdTime和utcTime在同一天，则显示HH:mm，否则显示MM-DD HH:mm
    extraTooltipContent = null, // 新增参数：额外的Tooltip内容，会显示在原有内容之后
    useClickDetail = false,
    disableDetail = false,
}) => {
    const { timeZone } = useTimeZone();

    if (!utcTime) return <span>--:--</span>;

    try {
        let finalFormat = format;

        // 如果显式要求显示日期
        if (showDate && !format.includes("MM-DD")) {
            finalFormat = "MM-DD " + format;
        }

        // 跨天检测逻辑：如果当前时间与STD不在同一天，且当前格式是HH:mm，则改为D-MMM
        if (stdTime && format === "HH:mm") {
            const isSameDay = areTimesOnSameDay(utcTime, stdTime, timeZone, airportCode, airportsMap);
            if (!isSameDay) {
                finalFormat = "MM-DD HH:mm";
            }
        }

        const result = formatFlightTime(utcTime, airportCode, timeZone, airportsMap, baseUtcTime, finalFormat);
        const { time, label, crossDayMark, timezone: actualTimezone, error } = result;

        // 检查是否在LOCAL模式下没有机场数据而回退到UTC
        const isLocalModeWithoutAirportData = timeZone === TIMEZONE_OPTIONS.LOCAL && actualTimezone === "UTC" && (!airportCode || !airportsMap || !airportsMap[airportCode]);

        if (result.isInvalid || result.error || !time) {
            return <span style={{ color: "#ff4d4f" }}>{result.time || "无效"}</span>;
        }

        // 如果在LOCAL模式下没有机场数据，回退到UTC显示并添加警告
        if (isLocalModeWithoutAirportData) {
            // 重新格式化为UTC时间
            const utcResult = formatFlightTime(utcTime, null, TIMEZONE_OPTIONS.UTC, {}, baseUtcTime, finalFormat);
            const content = (
                <div>
                    <div style={{ color: "#faad14", fontWeight: "bold" }}>警告：缺少机场时区数据</div>
                    <div>机场代码: {airportCode || "未提供"}</div>
                    <div>已回退到UTC时间显示以确保准确性</div>
                </div>
            );
            const display = (
                <span style={styles.timezoneDisplay}>
                    <span
                        style={{
                            ...styles.timeText,
                            color: "#faad14",
                            borderBottom: "2px dotted #faad14",
                        }}
                    >
                        {utcResult.time}
                    </span>
                    <sup
                        style={{
                            ...styles.timezoneIndicator,
                            color: "#faad14",
                            fontWeight: "bold",
                        }}
                    >
                        Z
                    </sup>
                </span>
            );

            if (disableDetail) {
                return display;
            }

            if (useClickDetail) {
                return <FlightTimeDetailTrigger title="时间详情" content={content}>{display}</FlightTimeDetailTrigger>;
            }

            return (
                <Tooltip title={content}>{display}</Tooltip>
            );
        }

        const shouldDisplayFlightSegmentCrossDayMark = !!baseUtcTime && !!crossDayMark;

        let icon = <GlobalOutlined />;
        let color = "#1890ff";

        if (timeZone === TIMEZONE_OPTIONS.BEIJING) {
            icon = <ClockCircleOutlined />;
            color = "#52c41a";
        } else if (timeZone === TIMEZONE_OPTIONS.LOCAL) {
            icon = <HomeOutlined />;
            color = "#722ed1";
        }

        const getShortTimezoneIndicator = () => {
            if (timeZone === TIMEZONE_OPTIONS.UTC) return "Z";
            if (timeZone === TIMEZONE_OPTIONS.BEIJING) return "B";
            if (timeZone === TIMEZONE_OPTIONS.LOCAL) return "LT";
            return "";
        };

        const formattedUtcTime = dayjs.utc(utcTime).format("YYYY-MM-DD HH:mm");
        // 确保时间包含日期信息
        const timezoneTimeWithDate = time.includes("-") ? time : dayjs.utc(utcTime).tz(actualTimezone).format("YYYY-MM-DD") + " " + time;

        if (showTimezone) {
            const content = (
                <div>
                    <div>
                        <span>{timezoneTimeWithDate}</span>
                        {shouldDisplayFlightSegmentCrossDayMark && <sup>{crossDayMark}</sup>} {label}
                    </div>
                    {label != "UTC" && <div>{formattedUtcTime} UTC</div>}
                    {extraTooltipContent && <div style={{ marginTop: 4, borderTop: "1px solid rgba(0,0,0,0.08)", paddingTop: 4 }}>{extraTooltipContent}</div>}
                </div>
            );
            const display = (
                <span style={styles.timezoneDisplay}>
                    <span style={{ ...styles.timeText, color }}>{time}</span>
                    {shouldDisplayFlightSegmentCrossDayMark && <sup style={{ ...styles.crossDayMark, color: crossDayMark.includes("+") ? "#52c41a" : "#ff4d4f" }}>{crossDayMark}</sup>}
                    <sup style={{ ...styles.timezoneIndicator, color }}>{getShortTimezoneIndicator()}</sup>
                </span>
            );

            if (disableDetail) {
                return display;
            }

            if (useClickDetail) {
                return <FlightTimeDetailTrigger title="时间详情" content={content}>{display}</FlightTimeDetailTrigger>;
            }

            return (
                <Tooltip title={content}>{display}</Tooltip>
            );
        } else {
            const content = (
                <div>
                    <div>
                        <span>{timezoneTimeWithDate}</span>
                        {shouldDisplayFlightSegmentCrossDayMark && <sup>{crossDayMark}</sup>}
                    </div>
                    {label != "UTC" && <div>{formattedUtcTime} UTC</div>}
                    {extraTooltipContent && <div style={{ marginTop: 4, borderTop: "1px solid rgba(0,0,0,0.08)", paddingTop: 4 }}>{extraTooltipContent}</div>}
                </div>
            );
            const display = (
                <div style={{ whiteSpace: "nowrap" }}>
                    <span style={styles.timeText}>{time}</span>
                    {shouldDisplayFlightSegmentCrossDayMark && (
                        <Tag color={crossDayMark.includes("+") ? "green" : "red"} style={{ marginLeft: 4 }}>
                            {crossDayMark}
                        </Tag>
                    )}
                </div>
            );

            if (disableDetail) {
                return display;
            }

            if (useClickDetail) {
                return <FlightTimeDetailTrigger title="时间详情" content={content}>{display}</FlightTimeDetailTrigger>;
            }

            return (
                <Tooltip title={content}>{display}</Tooltip>
            );
        }
    } catch (e) {
        console.error("时间显示错误 FlightTimeDisplay:", e, { utcTime, baseUtcTime, airportCode });
        return <span style={{ color: "#ff4d4f" }}>错误</span>;
    }
};

export default FlightTimeDisplay;
